import { useSelector } from "react-redux";
import { FocusEvent, useCallback, useMemo, useState } from "react";
import { Formik, FormikErrors, FormikProps, FormikTouched } from "formik";
import * as Yup from 'yup';
import { AttributeFormState } from "@idw-types/state/attribute-form-state";
import { CustomAttributeFieldContainer } from "../custom-attribute-field-container/custom-attribute-field-container";
import { AXNUIContentCustomAttributeField, SelectOption } from "@iddataweb/idw-common-ui/types";
import { getCustomAttributeFieldCustomizationData } from "@iddataweb/idw-common-ui/utils";
import { getActiveLanguage, getLanguageContent } from "@redux/selectors/language-selectors";
import { getConfigurationLayout } from "@redux/selectors/verification-configuration-selectors";
import styles from "./custom-attribute-container.less";

type CustomAttributeContainerProps = {
    fieldName: string;
    attribute: AttributeFormState;
    isSubmitting: boolean;
    onBlur: (event: FocusEvent<HTMLInputElement>) => void;
    setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>;
    handleAddNewSubmitValue: (payload: { attribute: AttributeFormState; inputData: any }) => void;
    handleUpdateSubmitValue: (payload: { attribute: AttributeFormState; inputData: any }) => void;
    handleUpdatePickerValue: (payload: { attribute: AttributeFormState; pickerData: any }) => void;
};

export const CustomAttributeContainer = (props: CustomAttributeContainerProps) => {
    const {
        fieldName,
        attribute,
        isSubmitting,
        onBlur,
        setFieldTouched: externalSetFieldTouched,
        setFieldValue: externalSetFieldValue,
        handleAddNewSubmitValue,
        handleUpdateSubmitValue,
        handleUpdatePickerValue
    } = props;

    const [formInitialValue] = useState(attribute.inputData);

    const activeLanguage = useSelector(getActiveLanguage);

    const layout = useSelector(getConfigurationLayout);
    const content = useSelector(getLanguageContent);
  
    const isRequired = useMemo(() => {
        return !(attribute.isOptional || attribute.optional)
    }, [attribute.isOptional, attribute.optional]);

    const customAttributeFieldDataMap = useMemo(() => {
        const finalMap: {
            [key: string]: {
                options: Array<SelectOption>;
                content: AXNUIContentCustomAttributeField;
            }
        } = {};
        attribute.fields.forEach(field => {
            finalMap[field.name] = getCustomAttributeFieldCustomizationData(`${attribute.id}`, field.name, field.customization, content, layout, activeLanguage);
        });
        return finalMap;
    }, [
        content.customAttributes[`${attribute.id}`],
        layout.customAttributes[`${attribute.id}`],
        activeLanguage
    ]);

    const CustomAttributeValidationSchema = useMemo(() => {
        const inputDataKeys = Object.keys(attribute.inputData);
        if (isRequired && inputDataKeys.length > 0) {
            const validationShape: { [key: string]: Yup.StringSchema<string, object, string, any, string> } = {};

            Object.keys(attribute.inputData).forEach(key => {
                const field = attribute.fields.find(atf => atf.name === key);
                const validationMessage = customAttributeFieldDataMap[field.name]?.content?.validation?.required || "MISSING_VALIDATION_MESSAGE";
                validationShape[key] = Yup.string().required(validationMessage);
            });

            return Yup.object().shape(validationShape);
        }

        return null;
    }, [customAttributeFieldDataMap]);

    const onSetSubmitValue = useCallback((payload: { inputData: { [key: string]: string } }) => {
        handleUpdateSubmitValue({ attribute, ...payload });
    }, [attribute, handleUpdateSubmitValue]);

    const onSetPickerValue = useCallback((payload: any) => {
        handleUpdatePickerValue({ attribute, ...payload });
    }, [attribute, handleUpdatePickerValue]);

    const onAddNewSubmitValue = useCallback((payload: { inputData: { [key: string]: string } }) => {
        handleAddNewSubmitValue({ attribute, inputData: payload });
    }, [attribute, handleAddNewSubmitValue]);


    const renderCustomAttributeComponent = (
        errors: FormikErrors<any>,
        touched: FormikTouched<any>,
        setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void,
        setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>
    ) => {
        const handleSetFieldTouched = (field: string, isTouched?: boolean, shouldValidate?: boolean) => {
            setFieldTouched(field, isTouched, shouldValidate);
            externalSetFieldTouched(fieldName, isTouched, shouldValidate);
        };

        const handleSetFieldValue = async (field: string, value: any, shouldValidate?: boolean) => {
            await setFieldValue(field, value, shouldValidate);
            await externalSetFieldValue(fieldName, value, shouldValidate);
        };

        return (
            <div
                id={`HTMLattr${attribute.id}`}
                className={styles.customAttributeContainer}
                style={{ display: attribute.isVisible ? "block" : "none" }}
            >
                {attribute.fields.map(field => (
                    <CustomAttributeFieldContainer
                        key={`custom-attr-field-${field.name}`}
                        attribute={attribute}
                        attributeTypeField={field}
                        customAttributeFieldData={customAttributeFieldDataMap[field.name]}
                        errors={errors}
                        touched={touched}
                        isSubmitting={isSubmitting}
                        onSetSubmitValue={onSetSubmitValue}
                        onSetPickerValue={onSetPickerValue}
                        onAddNewSubmitValue={onAddNewSubmitValue}
                        onBlur={onBlur}
                        setFieldTouched={handleSetFieldTouched}
                        setFieldValue={handleSetFieldValue}
                    />
                ))}
            </div >
        );
    };

    return (
        <Formik
            initialValues={formInitialValue}
            validateOnMount={true}
            validationSchema={CustomAttributeValidationSchema}
            onSubmit={undefined}
        >
            {({ errors, touched, setFieldTouched, setFieldValue }: FormikProps<any>) => (
                renderCustomAttributeComponent(
                    errors,
                    touched,
                    setFieldTouched,
                    setFieldValue
                )
            )}
        </Formik>
    );
}